/**
 * Per-type monster multipliers (AS3: IngamePopulator).
 * Five archetypes: Normal / Swarm / Armored / Giant / Runner.
 */

import type { MonsterType } from "./types.js";

export interface MonsterSpec {
  /** HP multiplier applied to monsterBaseHp * growthRate^wave. */
  hpMult: number;
  /** Speed multiplier on SPEED_MS_PER_CELL. >1 = slower (more ms per cell). */
  speedMult: number;
  /** Armor multiplier applied to armorGrowthRate * (wave-3). */
  armorMult: number;
  /** manaReward multiplier applied to base formula. */
  rewardMult: number;
  /** Approx monsters per wave range [min, max]. */
  countRange: [number, number];
}

export const MONSTER_SPECS: Record<MonsterType, MonsterSpec> = {
  // Baseline — no changes.
  Normal: {
    hpMult: 1.0,
    speedMult: 1.0,
    armorMult: 1.0,
    rewardMult: 1.0,
    countRange: [8, 16],
  },
  // Swarm: fast (1.6× speed → 1/1.6 ms-per-cell), fragile, tiny reward.
  // speedMult < 1 because our model is ms-per-cell: faster = fewer ms.
  Swarm: {
    hpMult: 0.35,
    speedMult: 0.625,
    armorMult: 0.3,
    rewardMult: 0.4,
    countRange: [40, 60],
  },
  // Armored: slow (0.75× speed → 1/0.75 ms-per-cell), tanky, rich reward.
  Armored: {
    hpMult: 1.2,
    speedMult: 1.333,
    armorMult: 3.0,
    rewardMult: 1.4,
    countRange: [8, 16],
  },
  // Giant: very slow (0.55×), massive HP (×5), few per wave, best reward.
  Giant: {
    hpMult: 5.0,
    speedMult: 1.818,
    armorMult: 1.0,
    rewardMult: 2.2,
    countRange: [2, 4],
  },
  // Runner: very fast (2×), weaker HP (×0.8), low armor, mid reward.
  Runner: {
    hpMult: 0.8,
    speedMult: 0.5,
    armorMult: 0.9,
    rewardMult: 1.0,
    countRange: [8, 16],
  },
};
